import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';

import { CharacterBubbleAnimation } from '../../../../constants/character-bubble-animations.ts';
import { StepGraderType } from '../../../../constants/grading.ts';
import { SPEECH_RECOGNITION_MESSAGES } from '../../../../constants/speech-recognition.ts';
import { selectCurrentLessonTaskStepKey } from '../../../../selectors/current-lesson-task-step-key.selector.ts';
import { triggerCharacterBubbleAnimationThunk } from '../../../../slices/app-state/thunks/trigger-character-bubble-animation.thunk.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible } from '../../../../slices/ball/ball.slice.ts';
import { setScriptReplacement } from '../../../../slices/scripts/scripts.slice.ts';
import { ScriptReplacementKey } from '../../../../slices/scripts/scripts.slice.types.ts';
import { computeStepWordIndicesThunk } from '../../../../slices/story-reading/thunks/compute-step-word-indices.thunk.ts';
import {
  PronunciationGrader,
  SentencePronunciationGraderResultDetails,
} from '../../../../types/pronunciation-grader.types.ts';

import { BaseAppStateChangeHandlerUtil } from '../base-app-state-change.handler.ts';

export class StoryBothReadSentenceStateChangeHandlerUtil extends BaseAppStateChangeHandlerUtil {
  private static _instance: StoryBothReadSentenceStateChangeHandlerUtil;
  private lastRecognizedText: string | null = null;
  private grader: PronunciationGrader<SentencePronunciationGraderResultDetails> | null = null;

  public static get instance(): StoryBothReadSentenceStateChangeHandlerUtil {
    if (!StoryBothReadSentenceStateChangeHandlerUtil._instance) {
      StoryBothReadSentenceStateChangeHandlerUtil._instance = new StoryBothReadSentenceStateChangeHandlerUtil();
    }
    return StoryBothReadSentenceStateChangeHandlerUtil._instance;
  }

  protected getHandlerName(): string {
    return 'StoryBothReadSentence';
  }

  protected override async onReadyToStart(): Promise<void> {
    await super.onReadyToStart();
    console.log(`${this.getHandlerName()} onReadyToStart`);
    await this.handleStartStep();
  }

  protected override async onRetry(): Promise<void> {
    await super.onRetry();
    console.log(`${this.getHandlerName()} onRetry`);
    await this.handleStartStep();
  }

  protected override async onPlaying(): Promise<void> {
    console.log(`${this.getHandlerName()} onPlaying`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    await this.handleAppReadSentenceWhileAnimatingBall();
    // Start recording after app finishes speaking
    await dispatch(updateAppStateThunk({ state: AppStateEnum.Recording }));
  }

  protected override async onRecording(): Promise<void> {
    console.log(`${this.getHandlerName()} onRecording`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    await this.handleBallAnimationWhileUserReadsSentence();
    try {
      if (!this.grader) {
        throw new Error('Grader not initialized');
      }
      const result = await this.grader?.startGrading();
      this.lastRecognizedText = result.details?.recognizedText ?? null;
      if (result.isCorrect) {
        await dispatch(updateAppStateThunk({ state: AppStateEnum.Correct }));
      } else {
        if (result.details?.firstIncorrectWord) {
          dispatch(
            setScriptReplacement({
              key: ScriptReplacementKey.IncorrectWord,
              value: result.details.firstIncorrectWord,
            })
          );
        }
        await dispatch(updateAppStateThunk({ state: AppStateEnum.Incorrect }));
      }
    } catch (error) {
      this.handleError(error as Error);
      await this.handleRetryAfterError();
    }
  }

  protected override async onIncorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onIncorrect`);
    await this.handleIncorrect(this.lastRecognizedText ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE);
  }

  protected override async onCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onCorrect`);
    await this.handleCorrect({
      assuming: false,
      answer: this.lastRecognizedText ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
    });
  }

  protected override async onAssumeCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onAssumeCorrect`);
    await this.handleCorrect({
      assuming: true,
      answer: this.lastRecognizedText ?? SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE,
    });
  }

  protected override async onReadyForNextStep(): Promise<void> {
    await super.onReadyForNextStep();
    console.log(`${this.getHandlerName()} onReadyForNextStep`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    const state = await this.reduxStoreUtil.getRootState();
    const stepKey = selectCurrentLessonTaskStepKey(state);
    if (this.lessonsUtil.isFinalStep(stepKey)) {
      await dispatch(triggerCharacterBubbleAnimationThunk(CharacterBubbleAnimation.SHAKE));
      await this.audioPlayerUtil.speakStep();
      await this.audioPlayerUtil.waitSpeakCompletion();
      dispatch(setBallIsVisible(true));
    } else {
      await dispatch(updateAppStateThunk({ state: AppStateEnum.MoveToNextStep }));
    }
  }

  protected override async onAborting(): Promise<void> {
    await super.onAborting();
    await this.grader?.abort();
  }

  private async handleStartStep(): Promise<void> {
    this.lastRecognizedText = null;
    const dispatch = await this.reduxStoreUtil.getDispatch();

    dispatch(computeStepWordIndicesThunk());

    // Initialize grader
    try {
      this.grader = await this.pronunciationGraderFactory.createGrader({
        type: StepGraderType.STORY_SENTENCE,
      });
    } catch (error) {
      this.handleError(error as Error);
    }

    // Speak step
    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    // Show ball when ready
    dispatch(setBallIsVisible(true));
  }
}

export const getStoryBothReadSentenceStateChangeHandlerUtil = (): StoryBothReadSentenceStateChangeHandlerUtil =>
  StoryBothReadSentenceStateChangeHandlerUtil.instance;
